/**
 * Shared hook for TV Wall mockups — pulls same data from existing endpoints.
 * Used by all 3 layout option pages so each option compares fair vs the same data.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const h = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const TEMPLATE_LABELS = {
  REV: { label: 'Revenue', prefix: 'RM', category: 'Revenue', color: '#10B981' },
  CPL: { label: 'Comm per LOT', prefix: '$', category: 'Sales', color: '#06B6D4' },
  CRT: { label: 'Closing Rate', suffix: '%', category: 'Sales', color: '#06B6D4' },
  CLS: { label: 'Total Close', category: 'Sales', color: '#06B6D4' },
  LEAD: { label: 'Leads', category: 'Marketing', color: '#3B82F6' },
  SUB: { label: 'Subscriber', category: 'Marketing', color: '#3B82F6' },
  POS: { label: 'Total Poster', category: 'Creative', color: '#8B5CF6' },
  VIA: { label: 'Total Video (Ads)', category: 'Creative', color: '#8B5CF6' },
  VID: { label: 'Total Video (Organic)', category: 'Creative', color: '#8B5CF6' },
  SIGC: { label: 'Signal Count', category: 'Signal', color: '#F43F5E' },
  SIG: { label: 'Signal Win Rate', suffix: '%', category: 'Signal', color: '#F43F5E' },
  PIP: { label: 'Pips Generated', category: 'Signal', color: '#F43F5E' },
  IBH: { label: 'Inhouse Hours', suffix: ' h', category: 'Operation', color: '#F59E0B' },
  ACA: { label: 'Active Client', category: 'Operation', color: '#F59E0B' },
  LOT: { label: 'Lot Generated', category: 'Operation', color: '#F59E0B' },
};

export const CATEGORY_COLOR = {
  Revenue: '#10B981', Sales: '#06B6D4', Marketing: '#3B82F6',
  Creative: '#8B5CF6', Signal: '#F43F5E', Operation: '#F59E0B',
};

export function fmt(v, opts = {}) {
  const n = Number(v || 0);
  if (opts.compact && Math.abs(n) >= 1000) {
    if (Math.abs(n) >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (Math.abs(n) >= 1000) return (n / 1000).toFixed(1) + 'k';
  }
  return n.toLocaleString(undefined, { maximumFractionDigits: opts.decimals ?? 2 });
}

export function statusColor(usagePct) {
  if (usagePct >= 80) return '#10B981';
  if (usagePct >= 50) return '#F59E0B';
  return '#E63946';
}

export function useTvWallData(period) {
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = async () => {
    try {
      const url = period
        ? `${API}/scorecard/ib-management/overview?period=${period}`
        : `${API}/scorecard/ib-management/overview`;
      const r = await axios.get(url, h());
      setData(r.data);
      setLastUpdated(new Date());
    } catch (e) {
      console.error('TV wall load failed', e);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5 * 60 * 1000); // refresh every 5min
    return () => clearInterval(id);
  }, [period]);

  // Day countdown — days remaining in current month
  // When a historical period is selected, day-based countdown reflects that
  // month, not today's date.
  const isCurrentPeriod = !period || period === new Date().toISOString().slice(0, 7);
  const [py, pm] = period ? period.split('-').map(Number) : [new Date().getFullYear(), new Date().getMonth() + 1];
  const refDate = isCurrentPeriod ? new Date() : new Date(py, pm - 1, 15); // mid-month for historical
  const lastDay = new Date(py, pm, 0).getDate();
  const currentDay = isCurrentPeriod ? refDate.getDate() : lastDay;
  const daysRemaining = Math.max(0, lastDay - currentDay);

  // Weekday-aware countdown (Mon-Fri only) — used for daily catch-up amount math.
  const isWeekday = (d) => { const wd = d.getDay(); return wd >= 1 && wd <= 5; };
  let weekdaysTotal = 0, weekdaysPassed = 0, weekdaysRemaining = 0;
  for (let day = 1; day <= lastDay; day++) {
    const d = new Date(py, pm - 1, day);
    if (isWeekday(d)) {
      weekdaysTotal += 1;
      if (day < currentDay) weekdaysPassed += 1;
      else if (day >= currentDay) weekdaysRemaining += 1;
    }
  }

  // Build metric list from aggregated_kpis + templates_meta + aggregated_targets
  let metrics = [];
  if (data?.aggregated_kpis) {
    const targets = data.aggregated_targets || {};
    Object.entries(data.aggregated_kpis).forEach(([code, value]) => {
      const meta = TEMPLATE_LABELS[code];
      if (!meta) return;
      const target = Number(targets[code] || 0);
      const numV = Number(value || 0);
      const pct = target > 0 ? Math.min(numV / target * 100, 999) : 0;
      const gap = Math.max(0, target - numV);
      // Daily catch-up: needed PER REMAINING WEEKDAY (inclusive of today)
      const dailyNeeded = (weekdaysRemaining > 0 && target > 0) ? gap / weekdaysRemaining : 0;
      const dailyOriginal = (weekdaysTotal > 0 && target > 0) ? target / weekdaysTotal : 0;
      const onTrackDaily = (weekdaysPassed > 0 && target > 0)
        ? numV / weekdaysPassed
        : 0;
      metrics.push({
        code, ...meta,
        value: numV,
        target,
        pct,
        gap,
        daily_needed: dailyNeeded,
        daily_original: dailyOriginal,
        daily_actual: onTrackDaily,
        status: target === 0 ? 'no_target' : pct >= 80 ? 'on_track' : pct >= 50 ? 'at_risk' : 'off_track',
      });
    });
    const order = (data.templates_meta || []).map(t => t.code);
    metrics.sort((a, b) => (order.indexOf(a.code) === -1 ? 99 : order.indexOf(a.code)) -
                          (order.indexOf(b.code) === -1 ? 99 : order.indexOf(b.code)));
  }

  // Top channels (already sorted by % from backend)
  const channels = (data?.channels || []).filter(c => c.linked).slice(0, 10);

  return { data, metrics, channels, daysRemaining, weekdaysTotal, weekdaysPassed, weekdaysRemaining, lastUpdated, reload: load };
}
